import { useMemo } from "react";
import {
  EntityPickerField,
  type TypedEntityPickerProps,
} from "./EntityPickerField";
import type { PickerEntity } from "./entity";
import { useRegistry } from "../../context/RegistryContext";
import type { DateString, InvoiceEntity, InvoiceId } from "../../api/types";
import {
  invoiceSender,
  isAccountInvoice,
  type SenderRegistry,
} from "../../logic/invoice-sender";

export type InvoicePickerFieldProps = TypedEntityPickerProps<InvoiceId> & {
  targetRange?: { start: DateString | null; end: DateString | null };
  usedInvoiceIds?: ReadonlySet<InvoiceId>;
  /**
   * Offer only invoices from one of the user's accounts (card statements).
   * An import can only be linked to those — the server refuses a payee's —
   * so the import pickers set this. Also turns off inline create, since a new
   * invoice made there could be a payee's.
   */
  accountInvoicesOnly?: boolean;
};

type Bucket = "overlapping" | "unused" | "other";

const BUCKET_LABEL: Record<Bucket, string> = {
  overlapping: "Overlapping date",
  unused: "Unused",
  other: "Other",
};

const BUCKET_ORDER: Record<Bucket, number> = {
  overlapping: 0,
  unused: 1,
  other: 2,
};

const computeBucket = (
  invoice: InvoiceEntity,
  targetRange: InvoicePickerFieldProps["targetRange"],
  usedInvoiceIds: ReadonlySet<InvoiceId> | undefined,
): Bucket => {
  if (targetRange?.start && targetRange.end) {
    if (
      invoice.dateReceived <= targetRange.end &&
      targetRange.start <= invoice.dueDate
    ) {
      return "overlapping";
    }
  }
  if (usedInvoiceIds && !usedInvoiceIds.has(invoice.id)) {
    return "unused";
  }
  return "other";
};

export const InvoicePickerField = ({
  targetRange,
  usedInvoiceIds,
  accountInvoicesOnly = false,
  ...rest
}: InvoicePickerFieldProps) => {
  // The invoices are what's picked; their senders are only named, so hidden
  // ones count.
  const { invoices: allInvoices, withHidden } = useRegistry();
  const grouped = targetRange !== undefined || usedInvoiceIds !== undefined;

  const invoices = useMemo(
    () =>
      accountInvoicesOnly
        ? (allInvoices ?? []).filter(isAccountInvoice)
        : (allInvoices ?? []),
    [allInvoices, accountInvoicesOnly],
  );

  const items = useMemo<PickerEntity[]>(() => {
    const base = invoices;
    const toItem = (inv: InvoiceEntity) => toPickerEntity(inv, withHidden);
    if (!grouped) {
      return base.map(toItem);
    }
    const withBucket = base.map((inv) => ({
      inv,
      bucket: computeBucket(inv, targetRange, usedInvoiceIds),
    }));
    withBucket.sort((a, b) => {
      const byBucket = BUCKET_ORDER[a.bucket] - BUCKET_ORDER[b.bucket];
      if (byBucket !== 0) return byBucket;
      return (a.inv.name || a.inv.id).localeCompare(
        b.inv.name || b.inv.id,
        undefined,
        { sensitivity: "base" },
      );
    });
    return withBucket.map((x) => toItem(x.inv));
  }, [invoices, grouped, targetRange, usedInvoiceIds, withHidden]);

  const groupBy = grouped
    ? (item: PickerEntity) => {
        const inv = invoices.find((i) => i.id === item.id);
        if (!inv) return "Other";
        return BUCKET_LABEL[computeBucket(inv, targetRange, usedInvoiceIds)];
      }
    : undefined;

  const allowCreate = accountInvoicesOnly ? false : rest.allowCreate;
  if (rest.multiple) {
    return (
      <EntityPickerField<InvoiceId>
        kind="invoice"
        items={items}
        groupBy={groupBy}
        {...rest}
        allowCreate={allowCreate}
      />
    );
  }
  return (
    <EntityPickerField<InvoiceId>
      kind="invoice"
      items={items}
      groupBy={groupBy}
      {...rest}
      allowCreate={allowCreate}
    />
  );
};

const toPickerEntity = (
  inv: InvoiceEntity,
  registry: SenderRegistry,
): PickerEntity => {
  const detail = inv.description ?? `Due ${inv.dueDate}`;
  const sender = invoiceSender(inv, registry);
  return {
    id: inv.id,
    name: inv.name || inv.id,
    description: sender?.entity ? `${sender.entity.name} · ${detail}` : detail,
    updated: inv.updated,
  };
};
